import React, { useMemo } from 'react';

const MiniConsensusChart = ({ predictions, home, away, homePercentage, awayPercentage, timeRange }) => {
  const chartData = useMemo(() => {
    if (predictions && predictions.length > 0) {
      const sortedPreds = [...predictions].sort((a, b) => {
        const timeA = a.timestamp?.getTime ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
        const timeB = b.timestamp?.getTime ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
        return timeA - timeB;
      });

      const dataPoints = [];
      let runningHomeConfidence = 0;
      let runningAwayConfidence = 0;
      let totalWeight = 0;

      sortedPreds.forEach((pred, index) => {
        const timestamp = pred.timestamp?.getTime ? pred.timestamp.getTime() : new Date(pred.timestamp).getTime();
        const confidence = pred.confidenceNumeric || 50;
        const winnerLower = (pred.winner || '').toLowerCase();
        const homeLower = (home || '').toLowerCase();
        const awayLower = (away || '').toLowerCase();

        const drawTerms = ['draw', 'tie', 'empate'];
        const isDrawPick = drawTerms.some(term => winnerLower.includes(term));

        let thisHomeConf, thisAwayConf;

        if (isDrawPick) {
          thisHomeConf = 50;
          thisAwayConf = 50;
        } else if (winnerLower.includes(homeLower) || homeLower.includes(winnerLower)) {
          thisHomeConf = confidence;
          thisAwayConf = 100 - confidence;
        } else if (winnerLower.includes(awayLower) || awayLower.includes(winnerLower)) {
          thisHomeConf = 100 - confidence;
          thisAwayConf = confidence;
        } else {
          thisHomeConf = 50;
          thisAwayConf = 50;
        }

        runningHomeConfidence += thisHomeConf;
        runningAwayConfidence += thisAwayConf;
        totalWeight += 1;

        const avgHomeConf = runningHomeConfidence / totalWeight;
        const avgAwayConf = runningAwayConfidence / totalWeight;

        dataPoints.push({
          time: new Date(timestamp),
          confidence: confidence,
          homePercentage: Math.round(avgHomeConf * 10) / 10,
          awayPercentage: Math.round(avgAwayConf * 10) / 10,
          volume: totalWeight,
          index: index,
          winner: pred.winner,
          originalConfidence: confidence
        });
      });

      return dataPoints;
    }
    return [];
  }, [predictions, home, away]);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="px-4 py-2 bg-black border-l-2 border-zinc-800">
        <div className="text-[9px] text-zinc-600 uppercase tracking-wider">No data</div>
      </div>
    );
  }

  // Slim but visible dimensions
  const chartWidth = 240;
  const chartHeight = 56;
  const padding = 3;
  const plotWidth = chartWidth - padding * 2;
  const plotHeight = chartHeight - padding * 2;

  const displayData = chartData;

  const createPath = (data, key) => {
    if (data.length === 0) return '';
    const points = data.map((d, i) => {
      const x = padding + (i / Math.max(data.length - 1, 1)) * plotWidth;
      const yValue = d[key] || 0;
      const y = padding + plotHeight - (yValue / 100) * plotHeight;
      return { x, y };
    });
    // Sharp lines, no curves
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
  };

  const homePath = createPath(displayData, 'homePercentage');
  const awayPath = createPath(displayData, 'awayPercentage');

  const currentData = chartData[chartData.length - 1];
  const previousData = chartData[chartData.length - 2];

  const displayHomePerc = Number(currentData?.homePercentage || homePercentage || 50);
  const displayAwayPerc = Number(currentData?.awayPercentage || awayPercentage || 50);
  const displayConfidence = Number(currentData?.confidence || 50);
  const confidenceTrend = previousData ?
    (displayConfidence > previousData.confidence ? 'up' :
     displayConfidence < previousData.confidence ? 'down' : 'flat') : 'flat';

  const currentWinner = displayHomePerc > displayAwayPerc ? home : away;
  const winnerColor = displayHomePerc > displayAwayPerc ? 'text-emerald-400' : 'text-red-400';

  return (
    <div className="flex items-stretch bg-black border-l-4 border-zinc-900 h-14">
      {/* Ultra Compact Stats */}
      <div className="flex items-center gap-4 px-3 py-2 bg-zinc-950/50">
        {/* Winner Badge */}
        <div className="flex flex-col">
          <div className="text-[7px] text-zinc-600 uppercase font-medium tracking-wider">Leader</div>
          <div className={`text-[11px] font-black ${winnerColor} uppercase truncate max-w-[60px]`}>
            {currentWinner}
          </div>
        </div>

        {/* Percentages */}
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <div className="text-[7px] text-zinc-600 uppercase">{home.substring(0, 3)}</div>
            <div className="text-[13px] font-black text-emerald-400">{displayHomePerc.toFixed(0)}%</div>
          </div>
          <div className="text-[8px] text-zinc-700">vs</div>
          <div className="flex flex-col items-center">
            <div className="text-[7px] text-zinc-600 uppercase">{away.substring(0, 3)}</div>
            <div className="text-[13px] font-black text-red-400">{displayAwayPerc.toFixed(0)}%</div>
          </div>
        </div>

        {/* Confidence */}
        <div className="flex flex-col items-center border-l border-zinc-800 pl-3">
          <div className="text-[7px] text-zinc-600 uppercase">Conf</div>
          <div className="flex items-center gap-0.5">
            <span className="text-[13px] font-black text-white">{displayConfidence.toFixed(0)}%</span>
            {confidenceTrend === 'up' && <span className="text-[10px] text-emerald-400">↑</span>}
            {confidenceTrend === 'down' && <span className="text-[10px] text-red-400">↓</span>}
          </div>
        </div>

        {/* Volume */}
        <div className="flex flex-col items-center">
          <div className="text-[7px] text-zinc-600 uppercase">Vol</div>
          <div className="text-[13px] font-black text-white">{Math.ceil(currentData.volume)}</div>
        </div>
      </div>

      {/* Skinny Sexy Chart */}
      <div className="flex-1 flex items-center px-2 bg-zinc-950/30">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-14">
          {/* Subtle grid */}
          <line x1={padding} y1={chartHeight/2} x2={plotWidth + padding} y2={chartHeight/2}
                stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />

          {/* Gradient fills */}
          <defs>
            <linearGradient id="homeGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
            </linearGradient>
            <linearGradient id="awayGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0"/>
            </linearGradient>
          </defs>

          {/* Area fills */}
          <path
            d={`${homePath} L ${plotWidth + padding},${chartHeight - padding} L ${padding},${chartHeight - padding} Z`}
            fill="url(#homeGrad)"
          />
          <path
            d={`${awayPath} L ${plotWidth + padding},${chartHeight - padding} L ${padding},${chartHeight - padding} Z`}
            fill="url(#awayGrad)"
          />

          {/* Sharp lines */}
          <path d={homePath} stroke="#10b981" strokeWidth="1.5" fill="none" />
          <path d={awayPath} stroke="#ef4444" strokeWidth="1.5" fill="none" />

          {/* Current point markers */}
          {displayData.length > 0 && (() => {
            const lastIndex = displayData.length - 1;
            const x = padding + plotWidth;
            const homeY = padding + plotHeight - (displayData[lastIndex].homePercentage / 100) * plotHeight;
            const awayY = padding + plotHeight - (displayData[lastIndex].awayPercentage / 100) * plotHeight;

            return (
              <>
                <circle cx={x} cy={homeY} r="2.5" fill="#10b981" stroke="#000" strokeWidth="1" />
                <circle cx={x} cy={awayY} r="2.5" fill="#ef4444" stroke="#000" strokeWidth="1" />
              </>
            );
          })()}
        </svg>
      </div>

      {/* Time Range Badge */}
      <div className="px-2 py-2 bg-zinc-950/50 border-l border-zinc-800 flex items-center">
        <div className="text-[7px] text-zinc-500 font-bold uppercase tracking-wider -rotate-90 whitespace-nowrap">
          {timeRange || '7D'}
        </div>
      </div>
    </div>
  );
};

export default MiniConsensusChart;